'use client';
import React, { useState, useEffect, useRef } from 'react';
import PublicHeader from '../public-thesis-portal/components/PublicHeader';
import PublicFooter from '../public-thesis-portal/components/PublicFooter';
import {
  BookOpen, User, Library, Shield, HelpCircle, Mail, Clock,
  CheckCircle2, AlertTriangle, FileText, Search, Key, Globe,
  UserCheck, Lock, RefreshCw, ChevronRight,
} from 'lucide-react';

// ─── Help page structure ─────────────────────────────────────────────────
// The content is organised into four main sections (Getting Started,
// Student Guide, Librarian Guide, Admin Guide) plus an FAQ. Each section has
// anchors so the sidebar TOC can smooth-scroll to them, and the currently-
// visible section is highlighted via an IntersectionObserver.

interface Section {
  id: string;
  title: string;
  icon: any;
  children?: { id: string; title: string }[];
}

const SECTIONS: Section[] = [
  { id: 'getting-started', title: 'Getting Started', icon: BookOpen, children: [
    { id: 'what-is-thesis-hub', title: 'What is the ThesisHub?' },
    { id: 'who-uses-it', title: 'Who uses it?' },
    { id: 'languages-supported', title: 'Languages supported' },
  ]},
  { id: 'student', title: 'Student Guide', icon: User, children: [
    { id: 'student-signup', title: 'Signing in' },
    { id: 'student-wizard', title: 'The submission wizard' },
    { id: 'student-orcid', title: 'ORCID iD — what and why' },
    { id: 'student-save', title: 'Saving progress' },
    { id: 'student-pdf', title: 'Preparing your PDF' },
    { id: 'student-statuses', title: 'Submission statuses' },
  ]},
  { id: 'librarian', title: 'Librarian Guide', icon: Library, children: [
    { id: 'librarian-role', title: 'Getting the librarian role' },
    { id: 'librarian-approve', title: 'Approving a submission' },
    { id: 'librarian-sendback', title: 'Sending back for revision' },
    { id: 'librarian-keywords', title: 'Editing keywords after approval' },
    { id: 'librarian-export', title: 'Exporting data' },
  ]},
  { id: 'admin', title: 'Admin Guide', icon: Shield, children: [
    { id: 'admin-users', title: 'Managing users and roles' },
    { id: 'admin-preauth', title: 'Pre-authorising librarians' },
    { id: 'admin-scopus', title: 'SCOPUS keyword sync' },
    { id: 'admin-cron', title: 'Scheduling the Elsevier cron' },
  ]},
  { id: 'faq', title: 'FAQ', icon: HelpCircle },
];

export default function HelpPage() {
  const [activeId, setActiveId] = useState('getting-started');
  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll-spy: highlight the currently-visible section in the sidebar.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const ids = SECTIONS.flatMap((s) => [s.id, ...(s.children?.map((c) => c.id) || [])]);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: [0, 0.5] },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f8fc]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <PublicHeader />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#111e3d] via-[#1a2e5a] to-[#243668] text-white">
        <div className="max-w-screen-2xl mx-auto px-6 xl:px-10 2xl:px-16 py-12">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="h-0.5 w-8 bg-[#c9a84c]" />
              <span className="text-[#c9a84c] text-xs font-600 tracking-wide uppercase">Documentation</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-800 mb-3">Help &amp; Documentation</h1>
            <p className="text-white/70 text-base leading-relaxed">
              Everything you need to know about using the Philips University Thesis Repository — from
              first sign-in to the hourly Elsevier sync. Jump to a section using the sidebar on the left.
            </p>
          </div>
        </div>
      </section>

      <main className="flex-1 max-w-screen-2xl mx-auto w-full px-6 xl:px-10 2xl:px-16 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
          {/* ── Sidebar TOC ── */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <nav className="bg-white border border-[#e2e6f0] rounded-2xl p-3 shadow-sm">
              <h2 className="px-2 pb-2 text-[11px] font-700 text-[#8892a8] uppercase tracking-wider">Contents</h2>
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const sectionActive = activeId === s.id || (s.children || []).some((c) => c.id === activeId);
                return (
                  <div key={s.id} className="mb-1">
                    <button
                      type="button"
                      onClick={() => scrollToId(s.id)}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs text-left font-600 transition-all ${
                        sectionActive ? 'bg-[#1a2e5a] text-white' : 'text-[#1a2e5a] hover:bg-[#f7f8fc]'
                      }`}>
                      <Icon size={13} />
                      <span>{s.title}</span>
                    </button>
                    {s.children && (
                      <div className="mt-0.5 ml-5 space-y-0.5 border-l border-[#e2e6f0] pl-2">
                        {s.children.map((c) => (
                          <button key={c.id} type="button" onClick={() => scrollToId(c.id)}
                            className={`w-full text-left text-xs px-2 py-1 rounded transition-all ${
                              activeId === c.id ? 'text-[#1a2e5a] font-600' : 'text-[#4a5578] hover:text-[#1a2e5a]'
                            }`}>
                            {c.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="mt-4 pt-3 border-t border-[#e2e6f0] px-2 text-xs text-[#8892a8] space-y-1">
                <p className="font-600 text-[#4a5578]">Need more help?</p>
                <a href="mailto:library@philipsuni.ac.cy" className="inline-flex items-center gap-1.5 text-[#1a2e5a] hover:text-[#c9a84c]">
                  <Mail size={11} /> library@philipsuni.ac.cy
                </a>
              </div>
            </nav>
          </aside>

          {/* ── Main content ── */}
          <div ref={contentRef} className="bg-white border border-[#e2e6f0] rounded-2xl p-8 md:p-10 shadow-sm space-y-12">

            {/* Getting Started ─────────────────────────────── */}
            <section id="getting-started">
              <SectionHeader icon={BookOpen} title="Getting Started" />

              <article id="what-is-thesis-hub" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">What is the ThesisHub?</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed">
                  ThesisHub is the official academic repository of Philips University Cyprus. It holds
                  master&apos;s theses and doctoral dissertations produced by the University&apos;s students,
                  following the Greek national repository standard. Every record carries bilingual
                  (English / Greek) metadata and a downloadable PDF of the thesis itself, subject to the
                  access rights the author selected.
                </p>
              </article>

              <article id="who-uses-it" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Who uses it?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <InfoCard icon={User} title="Students" text="Submit their completed or in-progress thesis through a guided bilingual wizard." />
                  <InfoCard icon={Library} title="Librarians" text="Review pending submissions, adjust metadata, approve or send back for revision, and export data." />
                  <InfoCard icon={Shield} title="Administrators" text="Manage users and roles, pre-authorise librarians, and schedule the SCOPUS keyword sync." />
                </div>
                <p className="text-sm text-[#4a5578] leading-relaxed mt-3">
                  The <em>public</em> side of the repository is accessible to anyone — no account needed. Only
                  submission, review and administration require signing in with a university account.
                </p>
              </article>

              <article id="languages-supported" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Languages supported</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed">
                  Metadata is captured in both English and Greek, with an optional third language. The
                  submission wizard has a three-way flag toggle <strong>EN / EL / Both</strong> in the top-left
                  corner, letting students show only the fields for one language if they wish. Final
                  exports (CSV, Excel, DOCX) always use the Greek labels mandated by the national standard,
                  with English appended after a slash for bilingual readability.
                </p>
              </article>
            </section>

            {/* Student Guide ───────────────────────────────── */}
            <section id="student" className="border-t border-[#e2e6f0] pt-10">
              <SectionHeader icon={User} title="Student Guide" />

              <article id="student-signup" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Signing in</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed">
                  Go to <strong>Submit Thesis</strong> in the top menu. You&apos;ll be prompted to sign in with
                  your Microsoft (Azure AD) or Google account. Use your <strong>university email</strong>
                  — the system uses that to tie the submission to your student record.
                </p>
                <CalloutTip
                  title="First time here?"
                  body="If you get an access-denied error, you may not have a student profile yet. Ask your department admin to add you, or email library@philipsuni.ac.cy."
                />
              </article>

              <article id="student-wizard" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">The submission wizard</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed mb-3">
                  The form walks you through <strong>10 steps</strong>, grouped into four phases. You can
                  jump back to any step you&apos;ve already completed using the sidebar. Required fields are
                  marked with a red asterisk and validated before you move forward.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <StepCard n={1} group="Identification" label="Student Identification" />
                  <StepCard n={2} group="Document" label="Document Type &amp; Rights" />
                  <StepCard n={3} group="Document" label="Titles (3 languages)" />
                  <StepCard n={4} group="Document" label="People &amp; ORCIDs" />
                  <StepCard n={5} group="Document" label="Institution &amp; Dates" />
                  <StepCard n={6} group="Content" label="Abstracts (3 languages)" />
                  <StepCard n={7} group="Content" label="Subjects &amp; Keywords" />
                  <StepCard n={8} group="Content" label="Identifiers &amp; Extras" />
                  <StepCard n={9} group="Finalize" label="PDF Upload" />
                  <StepCard n={10} group="Finalize" label="Review &amp; Submit" />
                </div>
                <p className="text-sm text-[#4a5578] leading-relaxed mt-3">
                  Each field has a <HelpCircle size={12} className="inline text-[#1a2e5a]" /> icon next to its
                  label — click it for a detailed explanation in a modal window. The ORCID field has a
                  particularly rich help modal with step-by-step registration instructions.
                </p>
              </article>

              <article id="student-orcid" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">ORCID iD — what and why</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed">
                  An <strong>ORCID iD</strong> is a free, unique 16-digit identifier for researchers —
                  think of it as a DOI for people. It distinguishes you from other researchers with the same
                  name and stays with you for your whole career across institutions and publishers.
                </p>
                <div className="bg-[#1a2e5a]/5 border border-[#1a2e5a]/20 rounded-xl p-4 mt-3">
                  <p className="text-sm font-700 text-[#1a2e5a] mb-1">When is an ORCID required?</p>
                  <ul className="text-sm text-[#4a5578] list-disc list-inside space-y-0.5">
                    <li><strong>Master&apos;s thesis (completed):</strong> required.</li>
                    <li><strong>Doctoral dissertation (PhD):</strong> recommended but not required — you may submit without one.</li>
                    <li><strong>Any thesis in progress:</strong> optional; you can add it later when you resubmit.</li>
                  </ul>
                </div>
                <div className="bg-[#f7f8fc] border border-[#e2e6f0] rounded-xl p-4 mt-3">
                  <h4 className="text-sm font-700 text-[#0f1a35] mb-2">How to create one (5 minutes)</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-[#4a5578]">
                    <li>Go to <a href="https://orcid.org/register" target="_blank" rel="noreferrer" className="text-[#1a2e5a] underline">orcid.org/register</a></li>
                    <li>Enter your name and your university email</li>
                    <li>Pick a password (≥ 8 chars, including a number and a symbol)</li>
                    <li>Verify your email via the link ORCID sends you</li>
                    <li>Copy the iD displayed at the top of your profile (format: <code className="bg-white px-1 rounded">0000-0000-0000-0000</code>) and paste it into the wizard</li>
                  </ol>
                </div>
                <CalloutTip title="One person = one ORCID" body="If you already got an ORCID during a previous degree, reuse that one. Don't create a second." />
              </article>

              <article id="student-save" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Saving progress</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed">
                  The wizard has a <strong>Save Progress</strong> button in the top-right. It saves to two
                  places: first to your account (so it survives logout and device changes), and second to
                  this browser&apos;s local storage (so it works even if you lose your connection). The next
                  time you open the form, your draft is restored automatically — including the step you
                  were on and your language-toggle preference. Progress also auto-saves quietly whenever
                  you navigate between steps.
                </p>
              </article>

              <article id="student-pdf" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Preparing your PDF</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed mb-2">
                  Upload your thesis as a PDF (max 50 MB per file). If your thesis exists in both English
                  and Greek, upload both; otherwise upload the single-language version and select its
                  language in the upload step.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
                  <p className="font-600 text-amber-800 mb-1 flex items-center gap-1.5">
                    <AlertTriangle size={13} /> PDF checklist
                  </p>
                  <ul className="list-disc list-inside text-amber-900 space-y-0.5">
                    <li>Text-searchable — if scanned, run OCR first</li>
                    <li>Not password-protected</li>
                    <li>Includes the signed title page</li>
                    <li>Fonts embedded so it renders correctly on other systems</li>
                  </ul>
                </div>
              </article>

              <article id="student-statuses" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Submission statuses</h3>
                <div className="space-y-2">
                  <StatusRow color="bg-blue-100 text-blue-700" label="Submitted" desc="Awaiting review by the library." />
                  <StatusRow color="bg-amber-100 text-amber-700" label="Sent Back" desc="The librarian asked for revisions. You'll see the comment at the top of your submission page." />
                  <StatusRow color="bg-emerald-100 text-emerald-700" label="Approved" desc="The metadata is accepted and queued for publication." />
                  <StatusRow color="bg-purple-100 text-purple-700" label="Published" desc="Visible on the public repository." />
                </div>
                <p className="text-sm text-[#4a5578] leading-relaxed mt-3">
                  You&apos;ll get an email notification at the <em>Student Email</em> you provided (step 1)
                  whenever the status changes.
                </p>
              </article>
            </section>

            {/* Librarian Guide ─────────────────────────────── */}
            <section id="librarian" className="border-t border-[#e2e6f0] pt-10">
              <SectionHeader icon={Library} title="Librarian Guide" />

              <article id="librarian-role" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Getting the librarian role</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed">
                  Librarian access is granted in one of two ways:
                </p>
                <ul className="list-disc list-inside text-sm text-[#4a5578] space-y-1 mt-2">
                  <li>An admin has <strong>pre-authorised</strong> your email address. In that case your
                      account is promoted to <em>librarian</em> the first time you sign in.</li>
                  <li>An admin promotes your existing account via the <em>Admin → User Management</em> tab.</li>
                </ul>
                <p className="text-sm text-[#4a5578] leading-relaxed mt-2">
                  Once you have the role, you&apos;ll see the <strong>Admin Panel</strong> link in the top menu.
                </p>
              </article>

              <article id="librarian-approve" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Approving a submission</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed mb-2">
                  Go to <strong>Admin → Pending Queue</strong>. Click a row to expand the full metadata and
                  PDF preview. When everything looks good:
                </p>
                <ol className="list-decimal list-inside text-sm text-[#4a5578] space-y-1">
                  <li>Review the metadata — correct any typos or formatting issues inline.</li>
                  <li>Adjust the SCOPUS keywords if needed (the student may have chosen too broadly).</li>
                  <li>Click <strong>Approve</strong>. The student is notified by email immediately.</li>
                </ol>
              </article>

              <article id="librarian-sendback" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Sending back for revision</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed">
                  If the submission needs changes, click <strong>Send Back</strong>, enter a comment
                  explaining what needs to be fixed, and submit. The student sees your comment as soon as
                  they sign back in — they can then edit and re-submit.
                </p>
              </article>

              <article id="librarian-keywords" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Editing keywords after approval</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed">
                  Even after a thesis is approved, you can still edit its SCOPUS keywords from the
                  <strong> Reports &amp; Export</strong> tab. Expand a row, click <em>Edit keywords</em>,
                  toggle your selections, and hit <em>Save</em>. The change is reflected in future
                  exports immediately.
                </p>
              </article>

              <article id="librarian-export" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Exporting data</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed mb-2">
                  Four export formats are available from <strong>Admin → Reports &amp; Export</strong>:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ExportCard label="CSV" desc="Flat records with national-standard Greek column labels; UTF-8 BOM so Excel renders Greek correctly." />
                  <ExportCard label="Excel (.xlsx)" desc="Styled workbook with one row per thesis, bilingual Greek/English headers." />
                  <ExportCard label="Word ZIP" desc="One .docx per thesis — title page plus a metadata table with Greek labels." />
                  <ExportCard label="PDFs ZIP" desc="Each thesis's original PDF(s) plus a per-folder metadata.txt and top-level MANIFEST." />
                </div>
                <p className="text-sm text-[#4a5578] leading-relaxed mt-3">
                  The green <strong>Export All &amp; Mark</strong> button runs all four formats at once
                  and flags the targeted records as <em>exported</em> so they&apos;re easy to filter out next
                  time.
                </p>
              </article>
            </section>

            {/* Admin Guide ─────────────────────────────────── */}
            <section id="admin" className="border-t border-[#e2e6f0] pt-10">
              <SectionHeader icon={Shield} title="Admin Guide" />

              <article id="admin-users" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Managing users and roles</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed">
                  Open <strong>Admin → User Management</strong>. You&apos;ll see every registered user with
                  a role dropdown (student / librarian / admin). Changes are applied immediately via a
                  SECURITY DEFINER RPC, so you don&apos;t need direct database access.
                </p>
                <CalloutTip title="Can't demote yourself" body="For safety, the role dropdown is disabled for your own account. If you truly need to change your own role, ask another admin, or run a direct SQL UPDATE." />
              </article>

              <article id="admin-preauth" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Pre-authorising librarians</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed">
                  To give someone librarian access <em>before they sign up</em>, open the
                  <strong> Pre-authorized emails</strong> sub-tab, add their email, pick the provider
                  (Any / Google / Azure AD), and the target role. Two Postgres triggers handle the rest:
                  when they sign in, <code className="bg-[#f7f8fc] px-1 rounded">apply_preauthorized_role</code>
                  elevates them on profile insert, and
                  <code className="bg-[#f7f8fc] px-1 rounded"> claim_preauthorized_librarian</code>
                  handles the case where they&apos;re already registered.
                </p>
              </article>

              <article id="admin-scopus" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">SCOPUS keyword sync</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed">
                  The wizard&apos;s keyword picker is populated from the SCOPUS ASJC subject categories.
                  These live in the <code className="bg-[#f7f8fc] px-1 rounded">elsevier_keywords</code>
                  table and are refreshed every hour by a cron job. You can also trigger a manual sync:
                  open <strong>Admin → Elsevier Keywords</strong> and click <strong>Sync from Elsevier API</strong>.
                </p>
              </article>

              <article id="admin-cron" className="mt-6 scroll-mt-24">
                <h3 className="text-lg font-700 text-[#0f1a35] mb-2">Scheduling the Elsevier cron</h3>
                <p className="text-sm text-[#4a5578] leading-relaxed mb-2">
                  The sync endpoint is <code className="bg-[#f7f8fc] px-1 rounded">GET /api/cron/sync-elsevier-keywords</code>.
                  It requires a <code className="bg-[#f7f8fc] px-1 rounded">X-Cron-Secret</code> header
                  matching your <code>CRON_SECRET</code> environment variable. See
                  <strong> README_SYNC.md</strong> in the repository root for the full setup including
                  Vercel Cron, server-side crontab, and Supabase pg_cron options.
                </p>
                <div className="bg-[#0f1a35] text-[#c9a84c] rounded-xl p-3 text-xs font-mono overflow-x-auto mt-2">
                  0 * * * * curl -sS -X POST -H &quot;X-Cron-Secret: $CRON_SECRET&quot; https://your-domain/api/cron/sync-elsevier-keywords
                </div>
              </article>
            </section>

            {/* FAQ ─────────────────────────────────────────── */}
            <section id="faq" className="border-t border-[#e2e6f0] pt-10">
              <SectionHeader icon={HelpCircle} title="Frequently Asked Questions" />
              <div className="mt-6 space-y-3">
                <FAQ q="Can I submit more than one thesis?" a="No — the system is designed around the national-standard one-deposit-per-degree workflow. If you have a legitimate reason to deposit a second thesis (different degree, different programme), contact the library and they can reset your submission state manually." />
                <FAQ q="What happens if I lose my connection while filling in the form?" a="Nothing lost. The form auto-saves to your account whenever you navigate between steps, and also keeps a local copy in your browser. When you come back, your progress is restored automatically." />
                <FAQ q="My Google sign-in asks me to click twice — is that normal?" a="No — that's a bug we fixed in recent versions. If it happens, the workaround is to refresh the page once after the redirect. If it keeps happening, let the admin know so we can check the deployment." />
                <FAQ q="The SCOPUS keywords list looks empty." a="Someone with admin access needs to sync the list once from the Elsevier API (Admin → Elsevier Keywords → Sync from Elsevier API). After that, the hourly cron keeps it fresh." />
                <FAQ q="I see fields labelled with Greek words even when I selected 'English only'." a="The flag toggle only hides bilingual content fields (titles, abstracts). Technical labels that are Greek by national-standard mandate (dc.uhtype, etc.) stay bilingual regardless, so the exported record remains valid." />
                <FAQ q="How do I change my password?" a="You don't — the system uses OAuth (Microsoft Azure AD or Google), so your password is managed by your identity provider. If you need to reset it, use your provider's flow." />
                <FAQ q="My PDF is over 50 MB, what do I do?" a="Most theses exceed 50 MB because of uncompressed high-resolution figures. Run a PDF compression tool (macOS Preview's 'Reduce File Size', Adobe Acrobat's 'Reduce File Size', or the free website smallpdf.com). Don't delete figures — the repository expects complete theses." />
                <FAQ q="Can the library see my passport/ID number?" a="Yes, but only staff with the librarian or admin role. This field exists so the library can verify the deposit against the legal identity on your degree record. It's never exposed on the public repository." />
                <FAQ q="Who do I contact for anything not covered here?" a={<>Email the library at <a className="underline text-[#1a2e5a]" href="mailto:library@philipsuni.ac.cy">library@philipsuni.ac.cy</a> — or call +357 22 441860 during office hours.</>} />
              </div>
            </section>

          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title }: { icon: any; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-1">
      <div className="w-10 h-10 rounded-xl bg-[#1a2e5a]/8 flex items-center justify-center">
        <Icon size={18} className="text-[#1a2e5a]" />
      </div>
      <h2 className="text-2xl font-800 text-[#0f1a35]">{title}</h2>
    </div>
  );
}

function InfoCard({ icon: Icon, title, text }: { icon: any; title: string; text: string }) {
  return (
    <div className="border border-[#e2e6f0] rounded-xl p-4 bg-[#f7f8fc]">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} className="text-[#1a2e5a]" />
        <h4 className="text-sm font-700 text-[#0f1a35]">{title}</h4>
      </div>
      <p className="text-xs text-[#4a5578] leading-relaxed">{text}</p>
    </div>
  );
}

function StepCard({ n, group, label }: { n: number; group: string; label: string }) {
  // Colour the phase badge per the wizard's four-group structure.
  const color = group === 'Identification' ? 'bg-blue-100 text-blue-700'
    : group === 'Document' ? 'bg-indigo-100 text-indigo-700'
    : group === 'Content' ? 'bg-amber-100 text-amber-700'
    : 'bg-emerald-100 text-emerald-700';
  return (
    <div className="flex items-center gap-3 border border-[#e2e6f0] rounded-xl p-3 bg-white">
      <span className="w-7 h-7 rounded-full bg-[#1a2e5a] text-white text-xs font-700 flex items-center justify-center flex-shrink-0">{n}</span>
      <div className="min-w-0">
        <div className="text-sm font-600 text-[#0f1a35] truncate" dangerouslySetInnerHTML={{ __html: label }} />
        <span className={`inline-block text-[10px] font-700 uppercase tracking-wider rounded px-1.5 py-0.5 mt-0.5 ${color}`}>{group}</span>
      </div>
    </div>
  );
}

function CalloutTip({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-[#1a2e5a]/5 border border-[#1a2e5a]/15 rounded-xl p-3 mt-3 flex items-start gap-2">
      <CheckCircle2 size={13} className="text-[#1a2e5a] flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-xs font-700 text-[#1a2e5a] mb-0.5">{title}</p>
        <p className="text-xs text-[#4a5578] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function StatusRow({ color, label, desc }: { color: string; label: string; desc: string }) {
  return (
    <div className="flex items-start gap-3 border border-[#e2e6f0] rounded-xl p-3 bg-white">
      <span className={`text-xs font-700 px-2.5 py-1 rounded-full ${color} whitespace-nowrap`}>{label}</span>
      <p className="text-sm text-[#4a5578] leading-relaxed">{desc}</p>
    </div>
  );
}

function ExportCard({ label, desc }: { label: string; desc: string }) {
  return (
    <div className="border border-[#e2e6f0] rounded-xl p-3 bg-white">
      <p className="text-sm font-700 text-[#0f1a35] mb-1 flex items-center gap-1.5">
        <FileText size={13} className="text-[#1a2e5a]" /> {label}
      </p>
      <p className="text-xs text-[#4a5578] leading-relaxed">{desc}</p>
    </div>
  );
}

function FAQ({ q, a }: { q: string; a: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#e2e6f0] rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-white hover:bg-[#f7f8fc] transition-all">
        <span className="text-sm font-600 text-[#0f1a35]">{q}</span>
        <ChevronRight size={14} className={`text-[#8892a8] flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 border-t border-[#e2e6f0] text-sm text-[#4a5578] leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}
